import React, { useContext } from 'react';

import { tv } from '../../tv';
import { SlotStyleContext } from './slot-context';

/**
 * ComplyKit structural slots: Header, Content, Footer, Bleed.
 *
 * Defined once and shared by every container (Card today; Dialog and Drawer
 * when they land) rather than each container owning bespoke parts. A header
 * means the same thing wherever it appears, so it is one component: that keeps
 * the vocabulary from multiplying by the number of containers and stops the
 * same concept drifting apart in each one. See
 * docs/specs/frontend/design-system/naming-and-composition.md.
 *
 * Slots take no styling props. They read the enclosing container's padding
 * from context, which is what lets a container set its inset once.
 */

/* Slots carry NO layout of their own. A header holding a title and an action
 * wants them side by side; a header holding a title and a description wants
 * them stacked. The slot cannot know which, and guessing produced a real bug:
 * a description rendered beside its heading instead of beneath it. Arranging
 * children is the caller's job, expressed with a layout component. */
/* Adjacent slots share one inset rather than each paying for it. A header
 * above content would otherwise put two full insets between the heading and
 * the body, reading as twice the intended space. The rule is expressed on the
 * following slot, so it holds however the caller orders or conditionally
 * renders them, and no call site has to zero a padding by hand.
 *
 * It does not apply across a divider. A rule makes the slot its own region,
 * so it needs its own inset: collapsing it there puts the content flush
 * against the line. */
const slotStyles = tv({
  slots: {
    header: [''],
    content: [''],
    footer: [''],
  },
  variants: {
    padding: {
      none: { header: '', content: '', footer: '' },
      compact: { header: 'ck:p-2', content: 'ck:p-2', footer: 'ck:p-2' },
      default: { header: 'ck:p-4', content: 'ck:p-4', footer: 'ck:p-4' },
      spacious: { header: 'ck:p-6', content: 'ck:p-6', footer: 'ck:p-6' },
    },
    /** A capped body scrolls rather than growing past its container. */
    scrollable: {
      true: { content: 'ck:overflow-y-auto ck:min-h-0' },
      false: {},
    },
    /** Separates the slot from its neighbour. Off by default: most containers
     * read better as one surface, and a rule is a deliberate choice. */
    divided: {
      true: {
        header: 'ck:border-b ck:border-border-divider',
        footer: 'ck:border-t ck:border-border-divider',
      },
      // Only an undivided (data-flush) header, or a body, collapses the slot
      // below it. A rule between them makes each side its own region, and a
      // Bleed above carries no inset to share, so the slot after it keeps its own.
      false: {
        content: 'ck:[[data-slot=header][data-flush]+&]:pt-0 ck:[[data-slot=content]+&]:pt-0',
        footer: 'ck:[[data-slot=header][data-flush]+&]:pt-0 ck:[[data-slot=content]+&]:pt-0',
      },
    },
  },
  defaultVariants: { padding: 'default', divided: false, scrollable: false },
});

interface SlotProps {
  /** The slot's content */
  children?: React.ReactNode;
  /** Draws a rule between this slot and the container's body */
  isDivided?: boolean;
  /** The element to render. Defaults to a div; headers often want a real header. */
  as?: 'div' | 'header' | 'footer' | 'section';
}

/** The top region of a container: a title, and usually an action or two. */
export const Header = React.forwardRef<HTMLElement, SlotProps>(function Header(
  { children, isDivided = false, as: As = 'div' },
  ref,
) {
  const { padding } = useContext(SlotStyleContext);
  const { header } = slotStyles({ padding, divided: isDivided });
  return (
    <As
      ref={ref as React.Ref<never>}
      className={header()}
      data-slot="header"
      data-flush={isDivided ? undefined : true}
    >
      {children}
    </As>
  );
});

interface ContentProps extends SlotProps {
  /**
   * Scrolls the body when its content exceeds the space available, which is
   * what a capped region wants. The scroll lives on the padded slot itself:
   * a nested scroll container inside the padding clips against the inset and
   * scrolls content that visibly fits.
   */
  isScrollable?: boolean;
  /** Caps the body's height, so a long list scrolls instead of growing. */
  maxHeight?: string | number;
  /**
   * Reserves a minimum height. Content that measures the space it is given,
   * a chart above all, needs the floor on this element: put it on a child and
   * the slot is still free to collapse, leaving the child nothing to measure.
   */
  minHeight?: string | number;
}

/** The body of a container. */
export const Content = React.forwardRef<HTMLElement, ContentProps>(function Content(
  { children, isScrollable = false, maxHeight, minHeight, as: As = 'div' },
  ref,
) {
  const { padding } = useContext(SlotStyleContext);
  const { content } = slotStyles({ padding, scrollable: isScrollable });
  const bounds =
    maxHeight === undefined && minHeight === undefined ? undefined : { maxHeight, minHeight };
  return (
    <As ref={ref as React.Ref<never>} className={content()} data-slot="content" style={bounds}>
      {children}
    </As>
  );
});

/** The bottom region of a container, typically actions. */
export const Footer = React.forwardRef<HTMLElement, SlotProps>(function Footer(
  { children, isDivided = false, as: As = 'div' },
  ref,
) {
  const { padding } = useContext(SlotStyleContext);
  const { footer } = slotStyles({ padding, divided: isDivided });
  return (
    <As ref={ref as React.Ref<never>} className={footer()} data-slot="footer">
      {children}
    </As>
  );
});

/**
 * Content that spans the container's full width: a table in a card, an image
 * at the top of one. Every design system shipping a padded container needs
 * this, which is why it is designed in rather than left to an escape hatch.
 *
 * The inset lives on the sibling slots rather than on the container, so Bleed
 * simply declines to apply one. A negative margin would be wrong here: it
 * would pull the content outside the container's own border.
 *
 * The rounding is applied here rather than on the container. A container that
 * clips cannot let anything through, including content placed to overlap its
 * edge on purpose, so the clipping belongs to the one child that needs it.
 */
const bleedStyles = tv({
  base: 'ck:overflow-hidden ck:[&:first-child]:rounded-t-3 ck:[&:last-child]:rounded-b-3',
  variants: { divided: { true: 'ck:border-t ck:border-border-divider', false: '' } },
  defaultVariants: { divided: false },
});

export const Bleed = React.forwardRef<
  HTMLDivElement,
  { children?: React.ReactNode; isDivided?: boolean }
>(function Bleed({ children, isDivided = false }, ref) {
  return (
    <div ref={ref} className={bleedStyles({ divided: isDivided })} data-slot="bleed">
      {children}
    </div>
  );
});
